import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'

import {
  fetchCommunityPostsPage,
  fetchCommunityPostById,
  fetchCommunityVoteDirectionsForPostsAndUser,
  setCommunityPostVote,
  netVoteScoreForPost,
  netWeeklyVoteScoreForPost,
  type CommunityPost,
  type CommunityVoteDirection,
} from '@/lib/communityPostData'
import { useArtistsData } from '@/contexts/ArtistsDataContext'
import { resolveCommunityPostPlaceLabel } from '@/lib/communityBoards'
import { formatShortTimeAgo } from '@/lib/communityTimeBuckets'
import { buildAccordionRegions } from '@/lib/locationRegions'
import {
  boardFeedPageTitle,
  resolveComposeCityBoardIdFromLocationSelection,
  resolveFeedCityLabelsFromLocationSelection,
} from '@/lib/boardFromLocation'

import { useAuthState } from '@/hooks/useAuth'
import { useCities } from '@/hooks/useCities'
import { useBoardUrlFilters } from '@/hooks/useBoardUrlFilters'
import type { BoardUrlFilterState } from '@/hooks/useBoardUrlFilters'

import { Button } from '@/components/ui/button'
import { AuthModal } from '@/components/auth/AuthModal'
import { Toast, useToast } from '@/components/ui/toast'
import { FullScreenDialog, FullScreenDialogContent } from '@/components/ui/full-screen-dialog'
import { CommunityPostAuthorLabel } from '@/components/community/CommunityPostAuthorLabel'
import { shouldShowVerifiedArtistCheckmark } from '@/lib/communityAuthorDisplay'
import { CommunityPostCarousel } from '@/components/community/CommunityPostCarousel'
import { CommunityPostDetailContent } from '@/components/community/CommunityPostDetailContent'
import { CommunityVoteBar } from '@/components/community/CommunityVoteBar'
import { BoardFilterBar } from '@/components/board/BoardFilterBar'

import { auth } from '@/lib/firebase'

interface PendingVote {
  readonly postId: string
  readonly direction: CommunityVoteDirection
}

interface BoardPageProps {
  /** When false (hidden persistent pane), skip feed fetches and error toasts. */
  isActive?: boolean
}

export default function BoardPage({ isActive = true }: BoardPageProps) {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { selectedLocations, sortMode, postTypeFilter, allLocationsText, applyBoardFilters } =
    useBoardUrlFilters()

  const detailPostId = searchParams.get('post')?.trim() ?? ''
  const detailModalOpen = detailPostId.length > 0

  const { user: authUser } = useAuthState()
  const { cities: launchedCities, cityCountryByCity, loading: citiesLoading } = useCities()
  const { availableArtistIds } = useArtistsData()
  const { toast, showToast, hideToast } = useToast()

  const [filterSheetOpen, setFilterSheetOpen] = useState(false)

  const [posts, setPosts] = useState<CommunityPost[]>([])
  const [pagingCursor, setPagingCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [voteDirectionByPostId, setVoteDirectionByPostId] = useState<
    ReadonlyMap<string, CommunityVoteDirection>
  >(new Map())

  const [authModalOpen, setAuthModalOpen] = useState(false)
  const pendingVoteRef = useRef<PendingVote | null>(null)

  const selectedLocationsKey = selectedLocations.join('\0')

  const locationAccordionRegions = useMemo(
    () => buildAccordionRegions(launchedCities, cityCountryByCity ?? {}),
    [launchedCities, cityCountryByCity],
  )

  const feedCities = useMemo(
    () =>
      resolveFeedCityLabelsFromLocationSelection({
        selectedLocations,
        allLocationsText,
        launchedCities,
      }),
    [selectedLocations, selectedLocationsKey, allLocationsText, launchedCities],
  )

  const feedCitiesKey = feedCities.join('\0')

  const composeCityBoardId = useMemo(
    () =>
      resolveComposeCityBoardIdFromLocationSelection({
        selectedLocations,
        allLocationsText,
        launchedCities,
      }),
    [selectedLocations, selectedLocationsKey, allLocationsText, launchedCities],
  )

  const boardPageTitle = useMemo(
    () =>
      boardFeedPageTitle({
        selectedLocations,
        allLocationsText,
        launchedCities,
        locationAccordionRegions,
      }),
    [selectedLocations, selectedLocationsKey, allLocationsText, launchedCities, locationAccordionRegions],
  )

  const appliedBoardFilters = useMemo<BoardUrlFilterState>(
    () => ({
      selectedLocations,
      sortMode,
      postTypeFilter,
    }),
    [selectedLocations, selectedLocationsKey, sortMode, postTypeFilter],
  )

  const handlePressVote = useCallback(
    async (postId: string, direction: CommunityVoteDirection) => {
      const viewerUid = auth.currentUser?.uid
      if (!viewerUid) {
        pendingVoteRef.current = { postId, direction }
        setAuthModalOpen(true)
        return
      }

      try {
        const { resultingDirection } = await setCommunityPostVote({
          postId,
          userId: viewerUid,
          pressedDirection: direction,
        })

        setVoteDirectionByPostId((current) => {
          const next = new Map(current)
          if (resultingDirection == null) next.delete(postId)
          else next.set(postId, resultingDirection)
          return next
        })

        const freshPost = await fetchCommunityPostById(postId)
        if (freshPost) {
          setPosts((current) => current.map((post) => (post.id === postId ? freshPost : post)))
        }
      } catch {
        showToast(String(t('community.voteFailed')))
      }
    },
    [showToast, t],
  )

  const replayPendingVoteAfterLogin = useCallback(() => {
    const pending = pendingVoteRef.current
    pendingVoteRef.current = null
    if (!pending) return
    window.setTimeout(() => void handlePressVote(pending.postId, pending.direction), 300)
  }, [handlePressVote])

  const showToastRef = useRef(showToast)
  showToastRef.current = showToast
  const translateRef = useRef(t)
  translateRef.current = t

  useEffect(() => {
    if (!isActive) return

    let cancelled = false

    async function loadInitialPosts() {
      if (launchedCities.length === 0) {
        return
      }

      if (feedCities.length === 0) {
        setPosts((currentPosts) => (currentPosts.length === 0 ? currentPosts : []))
        setPagingCursor((currentCursor) => (currentCursor == null ? currentCursor : null))
        setHasMore((currentHasMore) => (currentHasMore ? false : currentHasMore))
        setLoading((currentLoading) => (currentLoading ? false : currentLoading))
        return
      }

      setLoading(true)
      try {
        const page = await fetchCommunityPostsPage({
          cities: feedCities.length > 0 ? feedCities : launchedCities,
          sortMode,
          postTypeFilter,
        })
        if (cancelled) return
        setPosts(page.posts)
        setPagingCursor(page.nextCursor)
        setHasMore(page.nextCursor != null)
      } catch {
        if (!cancelled) {
          showToastRef.current(String(translateRef.current('community.loadFailed')))
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void loadInitialPosts()

    return () => {
      cancelled = true
    }
  }, [isActive, feedCities, feedCitiesKey, launchedCities.length, postTypeFilter, sortMode])

  useEffect(() => {
    if (!authUser?.uid || posts.length === 0) {
      setVoteDirectionByPostId(new Map())
      return
    }
    let cancelled = false
    void fetchCommunityVoteDirectionsForPostsAndUser(
      posts.map((post) => post.id),
      authUser.uid,
    ).then((map) => {
      if (!cancelled) setVoteDirectionByPostId(map)
    })
    return () => {
      cancelled = true
    }
  }, [authUser?.uid, posts])

  const handleLoadMore = async () => {
    if (feedCities.length === 0 || !pagingCursor || loadingMore || !hasMore) return
    setLoadingMore(true)
    try {
      const page = await fetchCommunityPostsPage({
        cities: feedCities.length > 0 ? feedCities : launchedCities,
        sortMode,
        postTypeFilter,
        cursor: pagingCursor,
      })
      if (page.posts.length === 0) {
        setHasMore(false)
      } else {
        setPosts((current) => [...current, ...page.posts])
        setPagingCursor(page.nextCursor)
        setHasMore(page.nextCursor != null)
      }
    } catch {
      showToast(String(t('community.paginationFailed')))
    } finally {
      setLoadingMore(false)
    }
  }

  const composerHref = composeCityBoardId
    ? `/board/new?board=${encodeURIComponent(composeCityBoardId)}`
    : '/board/new'

  const composePromptIcon = (
    <span className="flex shrink-0 flex-col items-center pt-1">
      <span className="flex items-center justify-center rounded-md p-1.5 text-muted-foreground">
        <Plus className="h-5 w-5" strokeWidth={2} />
      </span>
    </span>
  )

  const composePromptCard = (
    <Link
      to={composerHref}
      aria-label={String(t('community.newPostAria'))}
      className="flex gap-2 rounded-xl border border-dashed border-border/80 bg-card/50 py-3 pr-3 pl-1 shadow-sm transition-colors hover:border-border hover:bg-card"
    >
      {composePromptIcon}
      <div
        className="pointer-events-none min-h-[72px] min-w-0 flex-1 rounded-md border border-input bg-background px-3 py-2.5 text-sm text-muted-foreground"
        aria-hidden
      >
        {t('board.composePrompt')}
      </div>
    </Link>
  )

  const openPostDetail = useCallback(
    (postId: string) => {
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.set('post', postId)
      setSearchParams(newSearchParams)
    },
    [searchParams, setSearchParams],
  )

  const handleClosePostDetail = useCallback(
    (open: boolean) => {
      if (open) return
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.delete('post')
      setSearchParams(newSearchParams, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const handlePostUpdatedFromDetail = useCallback((updatedPost: CommunityPost) => {
    setPosts((currentPosts) =>
      currentPosts.map((existingPost) =>
        existingPost.id === updatedPost.id ? updatedPost : existingPost,
      ),
    )
  }, [])

  const handleViewerVoteDirectionChangeFromDetail = useCallback(
    (postId: string, direction: CommunityVoteDirection | null) => {
      setVoteDirectionByPostId((currentDirections) => {
        const nextDirections = new Map(currentDirections)
        if (direction == null) nextDirections.delete(postId)
        else nextDirections.set(postId, direction)
        return nextDirections
      })
    },
    [],
  )

  return (
    <>
      <div className="min-h-screen bg-background pb-bottom-nav">
        <Toast message={toast.message} isVisible={toast.isVisible} onClose={hideToast} />

        <div className="sticky-header-notch-backdrop sticky top-0 shrink-0 md:top-[52px] z-40 bg-background/95 backdrop-blur-sm border-b border-border/50">
          <div className="phone-container-padded space-y-3 pt-0 pb-3">
            <div className="min-w-0 space-y-1">
              <h1 className="text-page-title text-foreground truncate">{boardPageTitle}</h1>
              <p className="text-xs text-muted-foreground">{t('board.pageSubtitle')}</p>
            </div>

            <BoardFilterBar
              appliedFilters={appliedBoardFilters}
              onApplyFilters={applyBoardFilters}
              filterSheetOpen={filterSheetOpen}
              onFilterSheetOpenChange={setFilterSheetOpen}
            />
          </div>
        </div>

        <div className="phone-container-padded pt-4">
          <div className="space-y-3">
            {composePromptCard}

            {(citiesLoading || loading) && posts.length === 0 ? (
              <>
                {[0, 1, 2].map((index) => (
                  <div key={index} className="flex gap-2 rounded-xl border border-border bg-card py-3 pr-3 pl-1">
                    <div className="h-16 w-10 shrink-0 animate-pulse rounded-md bg-muted/50" />
                    <div className="flex-1 space-y-3">
                      <div className="h-3 w-1/3 animate-pulse rounded bg-muted/50" />
                      <div className="h-4 w-2/3 animate-pulse rounded bg-muted/50" />
                      <div className="h-24 animate-pulse rounded-lg bg-muted/50" />
                    </div>
                  </div>
                ))}
              </>
            ) : posts.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">{t('board.emptyState')}</p>
            ) : (
              <>
                {posts.map((post) => {
                const viewerDirection = voteDirectionByPostId.get(post.id) ?? null
                const bodyText = post.body?.trim() ?? ''
                const authorName =
                  post.authorDisplayName?.trim() || String(t('community.anonymousHandle'))
                const showVerifiedArtistCheckmark = shouldShowVerifiedArtistCheckmark({
                  authorRole: post.authorRole,
                  artistId: post.artistId,
                  liveArtistIds: availableArtistIds,
                })
                const ageLabel = formatShortTimeAgo(post.createdAt)
                const placeLabel = resolveCommunityPostPlaceLabel({
                  boardId: post.boardId,
                  cityDisplayLabel: post.cityDisplayLabel,
                  worldwideLabel: String(t('community.worldwide')),
                })
                const netScore =
                  sortMode === 'topWeek'
                    ? netWeeklyVoteScoreForPost(post)
                    : netVoteScoreForPost(post)

                return (
                  <article
                    key={post.id}
                    onClick={() => openPostDetail(post.id)}
                    className="flex gap-2 rounded-xl border border-border bg-card py-3 pr-3 pl-1 shadow-sm cursor-pointer transition-colors hover:bg-card/80"
                  >
                    <CommunityVoteBar
                      layout="sidebar"
                      viewerDirection={viewerDirection}
                      netScore={netScore}
                      commentCount={post.commentCount}
                      onPressUpvote={() => void handlePressVote(post.id, 'up')}
                      onPressDownvote={() => void handlePressVote(post.id, 'down')}
                    />

                    <div className="min-w-0 flex-1 space-y-3">
                      <div className="space-y-1">
                        <h2 className="text-base font-semibold leading-snug text-foreground">
                          {post.title}
                        </h2>

                        {bodyText.length > 0 && (
                          <p className="whitespace-pre-wrap text-sm text-foreground/80 line-clamp-4">
                            {bodyText}
                          </p>
                        )}
                      </div>

                      {post.imageUrls.length > 0 && (
                        <CommunityPostCarousel imageUrls={post.imageUrls} />
                      )}

                      {post.commentCount > 0 && (
                        <p className="text-xs font-medium text-muted-foreground">
                          {t('board.commentCount', { count: post.commentCount })}
                        </p>
                      )}

                      <div className="flex items-center justify-between gap-3">
                        <CommunityPostAuthorLabel
                          authorDisplayName={authorName}
                          showVerifiedArtistCheckmark={showVerifiedArtistCheckmark}
                          className="min-w-0"
                        />
                        <p className="shrink-0 text-xs text-muted-foreground">
                          {placeLabel}
                          <span aria-hidden> · </span>
                          {ageLabel}
                        </p>
                      </div>
                    </div>
                  </article>
                )
              })}

                {hasMore && (
                  <div className="flex justify-center pt-4 pb-2">
                    <Button
                      variant="outline"
                      type="button"
                      disabled={loadingMore}
                      onClick={() => void handleLoadMore()}
                    >
                      {loadingMore ? t('community.loadingMore') : t('community.loadMore')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <AuthModal
          dismissible
          isOpen={authModalOpen}
          onOpenChange={setAuthModalOpen}
          onSuccess={replayPendingVoteAfterLogin}
          source="board-feed"
        />
      </div>

      <FullScreenDialog open={detailModalOpen} onOpenChange={handleClosePostDetail}>
        <FullScreenDialogContent>
          {detailPostId.length > 0 ? (
            <CommunityPostDetailContent
              postId={detailPostId}
              onBack={() => handleClosePostDetail(false)}
              onPostUpdated={handlePostUpdatedFromDetail}
              onViewerVoteDirectionChange={handleViewerVoteDirectionChangeFromDetail}
            />
          ) : null}
        </FullScreenDialogContent>
      </FullScreenDialog>
    </>
  )
}
